import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Constants } from "@babylonjs/core/Engines/constants";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { AURAMENNONE_AURA } from "@arena/character-data";

type Kind = "glow" | "pool" | "ring" | "dot";

const cached = new WeakMap<Scene, Map<Kind, DynamicTexture>>();

/** Soft golden shapes drawn once per scene: the halo, the light on the floor, the fear ring, a spark. */
function texture(scene: Scene, kind: Kind): DynamicTexture {
  let cache = cached.get(scene);
  if (!cache) cached.set(scene, (cache = new Map()));
  const old = cache.get(kind);
  if (old) return old;
  const size = kind === "dot" ? 16 : 256;
  const t = new DynamicTexture(`aura-${kind}`, { width: size, height: size }, scene, false);
  const g = t.getContext() as unknown as CanvasRenderingContext2D;
  const c = size / 2;
  g.clearRect(0, 0, size, size);
  if (kind === "ring") {
    // The edge of the fear: a bright double ring with short rays inward.
    g.strokeStyle = "rgba(255,214,120,1)";
    g.lineWidth = 7;
    g.beginPath();
    g.arc(c, c, 120, 0, Math.PI * 2);
    g.stroke();
    g.lineWidth = 3;
    g.setLineDash([14, 10]);
    g.beginPath();
    g.arc(c, c, 104, 0, Math.PI * 2);
    g.stroke();
    g.setLineDash([]);
    for (let i = 0; i < 24; i++) {
      const a = (i * Math.PI) / 12;
      g.beginPath();
      g.moveTo(c + Math.cos(a) * 112, c + Math.sin(a) * 112);
      g.lineTo(c + Math.cos(a) * 92, c + Math.sin(a) * 92);
      g.stroke();
    }
    const inside = g.createRadialGradient(c, c, 60, c, c, 120);
    inside.addColorStop(0, "rgba(255,190,80,0)");
    inside.addColorStop(1, "rgba(255,190,80,0.22)");
    g.fillStyle = inside;
    g.beginPath();
    g.arc(c, c, 120, 0, Math.PI * 2);
    g.fill();
  } else {
    const gradient = g.createRadialGradient(c, c, 0, c, c, c);
    if (kind === "dot") {
      gradient.addColorStop(0, "rgba(255,255,255,1)");
      gradient.addColorStop(0.5, "rgba(255,255,255,0.6)");
    } else {
      // Soft all the way to the edge: a glow, never a visible square.
      gradient.addColorStop(0, "rgba(255,240,180,1)");
      gradient.addColorStop(0.3, "rgba(255,200,90,0.75)");
      gradient.addColorStop(0.65, "rgba(255,160,50,0.25)");
    }
    gradient.addColorStop(1, "rgba(255,150,40,0)");
    g.fillStyle = gradient;
    g.fillRect(0, 0, size, size);
  }
  t.hasAlpha = true;
  t.update(false);
  cache.set(kind, t);
  return t;
}

function additive(scene: Scene, name: string, kind: Kind): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseTexture = texture(scene, kind);
  m.useAlphaFromDiffuseTexture = true;
  m.emissiveColor = new Color3(1.6, 1.25, 0.6);
  m.disableLighting = true;
  m.backFaceCulling = false;
  m.alphaMode = Constants.ALPHA_ADD;
  return m;
}

/**
 * Auramennone's aura, impossible to miss: a golden halo behind him, a pool of
 * light at his feet and sparks rising around him. At maximum everything is
 * bigger and a ring on the ground shows where enemies are frightened (the
 * real server radius).
 */
export class AuraView {
  private readonly halo: Mesh;
  private readonly pool: Mesh;
  private readonly ring: Mesh;
  private readonly materials: StandardMaterial[];
  private readonly sparks: ParticleSystem;
  private readonly emitter = new Vector3();
  private age = 0;
  private strength = 0;

  constructor(scene: Scene) {
    const haloMaterial = additive(scene, "aura-halo", "glow");
    const poolMaterial = additive(scene, "aura-pool", "pool");
    const ringMaterial = additive(scene, "aura-ring", "ring");
    this.materials = [haloMaterial, poolMaterial, ringMaterial];

    this.halo = CreatePlane("aura-halo", { size: 1 }, scene);
    this.halo.billboardMode = 7; // BILLBOARDMODE_ALL: always facing the camera, like the sprites.
    this.halo.material = haloMaterial;
    this.pool = CreateGround("aura-pool", { width: 1, height: 1 }, scene);
    this.pool.material = poolMaterial;
    this.ring = CreateGround("aura-ring", { width: 1, height: 1 }, scene);
    this.ring.material = ringMaterial;
    for (const mesh of [this.halo, this.pool, this.ring]) mesh.isPickable = false;
    // Behind the fighter's sprite in the draw order.
    this.halo.renderingGroupId = 0;

    const sparks = new ParticleSystem("aura-sparks", 220, scene);
    sparks.particleTexture = texture(scene, "dot");
    sparks.emitter = this.emitter;
    sparks.minEmitBox = new Vector3(-0.55, 0, -0.55);
    sparks.maxEmitBox = new Vector3(0.55, 0.4, 0.55);
    sparks.color1 = new Color4(1, 0.86, 0.4, 1);
    sparks.color2 = new Color4(1, 0.6, 0.15, 1);
    sparks.colorDead = new Color4(1, 0.4, 0.05, 0);
    sparks.minSize = 0.1;
    sparks.maxSize = 0.26;
    sparks.minLifeTime = 0.5;
    sparks.maxLifeTime = 1.1;
    sparks.direction1 = new Vector3(-0.25, 1, -0.25);
    sparks.direction2 = new Vector3(0.25, 1.8, 0.25);
    sparks.minEmitPower = 0.9;
    sparks.maxEmitPower = 1.8;
    sparks.gravity = new Vector3(0, 0.6, 0);
    sparks.blendMode = ParticleSystem.BLENDMODE_ADD;
    sparks.emitRate = 0;
    sparks.start();
    this.sparks = sparks;
    this.show(false, false);
  }

  /** Every frame: where he stands, which aura he has (none, normal, maximum), where the camera is. */
  update(feet: Vector3, active: boolean, max: boolean, dt: number, camera: Vector3 | undefined): void {
    this.age += dt;
    // Fade in and out instead of popping.
    this.strength = Math.max(0, Math.min(1, this.strength + (active ? dt * 5 : -dt * 4)));
    const on = this.strength > 0.01;
    this.show(on, on && max);
    this.sparks.emitRate = active ? (max ? 140 : 70) : 0;
    if (!on) return;
    const pulse = 1 + Math.sin(this.age * 6) * 0.08;
    const size = (max ? 4.2 : 3.2) * pulse;
    this.halo.position.set(feet.x, feet.y + 1.25, feet.z);
    // Behind him as seen from the camera, so the glow frames the sprite instead of covering it.
    if (camera) {
      const dx = feet.x - camera.x;
      const dz = feet.z - camera.z;
      const d = Math.hypot(dx, dz) || 1;
      this.halo.position.x += (dx / d) * 0.6;
      this.halo.position.z += (dz / d) * 0.6;
    }
    this.halo.scaling.set(size * 0.8, size, 1);
    this.pool.position.set(feet.x, feet.y + 0.04, feet.z);
    this.pool.scaling.set((max ? 3.6 : 2.8) * pulse, 1, (max ? 3.6 : 2.8) * pulse);
    this.ring.position.set(feet.x, feet.y + 0.05, feet.z);
    this.ring.scaling.set(AURAMENNONE_AURA.fearRadius * 2, 1, AURAMENNONE_AURA.fearRadius * 2);
    this.ring.rotation.y = this.age * 0.8;
    this.materials[0]!.alpha = this.strength * (max ? 1 : 0.85);
    this.materials[1]!.alpha = this.strength;
    this.materials[2]!.alpha = this.strength * (0.55 + Math.sin(this.age * 5) * 0.25);
    this.emitter.copyFrom(feet);
  }

  private show(aura: boolean, max: boolean): void {
    this.halo.setEnabled(aura);
    this.pool.setEnabled(aura);
    this.ring.setEnabled(max);
  }

  dispose(): void {
    this.sparks.dispose();
    for (const mesh of [this.halo, this.pool, this.ring]) mesh.dispose();
    for (const m of this.materials) m.dispose();
  }
}
